import { z } from 'zod'
import {
  AccountStatsByStatusKeySchema,
  AccountRoleSchema,
  type ReportWorkflowField,
  ReportWorkflowFieldSchema,
  ReportCustomFieldAccountDateSchema,
  ReportCustomFieldAggregationTypeSchema,
} from '../report'
import { RatioIdSchema } from '../ratio'
import { AccountStatusIdSchema, WorkflowStageIdSchema } from '../ids'
import { CustomFieldIdSchema } from '../customField'

const ContactPitchMetricSchema = z.enum(['contact.pitch', 'contact.pitches'])
const ContactDaysWorkedSchema = z.literal('contact.daysWorked')
const MeetingCheckinMetricSchema = z.enum(['meeting.checkin', 'meetingAttendance.count'])
const ConversationMetricSchema = z.enum(['conversation.averageScore', 'conversation.count', 'conversation.activeHours'])

export const WorkflowStageMetricSchema = z.templateLiteral([
  AccountRoleSchema,
  '|',
  WorkflowStageIdSchema,
  '|',
  AccountStatsByStatusKeySchema.extract(['count']),
])
export type WorkflowStageMetric = z.infer<typeof WorkflowStageMetricSchema>
export const isWorkflowStageMetric = (val: string): val is WorkflowStageMetric =>
  WorkflowStageMetricSchema.safeParse(val).success

export const isWorkflowStageMetricV2 = (val: string): val is ReportWorkflowField =>
  ReportWorkflowFieldSchema.safeParse(val).success

export const AccountStatusMetricSchema = z.union([
  AccountStatusIdSchema,
  z.templateLiteral([
    AccountRoleSchema,
    '.',
    AccountStatusIdSchema,
    '.',
    AccountStatsByStatusKeySchema.extract(['count']),
  ]),
])
export type AccountStatusMetric = z.infer<typeof AccountStatusMetricSchema>
export const isAccountStatusMetric = (val: string): val is AccountStatusMetric =>
  AccountStatusMetricSchema.safeParse(val).success

export const CustomFieldMetricSchema = z.union([
  CustomFieldIdSchema,
  z.templateLiteral([
    'cf',
    '|',
    AccountRoleSchema,
    '|',
    ReportCustomFieldAggregationTypeSchema.extract(['count']),
    '|',
    CustomFieldIdSchema,
    '|',
    ReportCustomFieldAccountDateSchema,
  ]),
])
export type CustomFieldMetric = z.infer<typeof CustomFieldMetricSchema>
export const isCustomFieldMetric = (val: string): val is CustomFieldMetric =>
  CustomFieldMetricSchema.safeParse(val).success

export const GameMetricIdSchema = z.union([
  ContactPitchMetricSchema,
  ContactDaysWorkedSchema,
  MeetingCheckinMetricSchema,
  AccountStatusMetricSchema,
  CustomFieldMetricSchema,
  WorkflowStageMetricSchema,
  ReportWorkflowFieldSchema,
  ConversationMetricSchema,
  RatioIdSchema,
])
export type GameMetricId = z.infer<typeof GameMetricIdSchema>
export const isGameMetricId = (val: string): val is GameMetricId => GameMetricIdSchema.safeParse(val).success

const ScorableBaseSchema = z.object({
  name: z.string(),
})

export const ScorableWorkflowStage = z.object({
  ...ScorableBaseSchema.shape,
  field: z.union([WorkflowStageMetricSchema, ReportWorkflowFieldSchema]),
  type: z.literal('exists'),
})

export const ScorableAccountStatus = z.object({
  ...ScorableBaseSchema.shape,
  field: AccountStatusMetricSchema,
  type: z.literal('exists'),
})

export const ScorableContactPitch = z.object({
  ...ScorableBaseSchema.shape,
  field: ContactPitchMetricSchema,
  type: z.literal('exists'),
})

export const ScorableContactDaysWorked = z.object({
  ...ScorableBaseSchema.shape,
  field: ContactDaysWorkedSchema,
  type: z.literal('exists'),
})

export const ScorableMeeting = z.object({
  ...ScorableBaseSchema.shape,
  field: MeetingCheckinMetricSchema,
  type: z.literal('exists'),
})

export const ScorableConversation = z.object({
  ...ScorableBaseSchema.shape,
  field: ConversationMetricSchema,
  type: z.literal('exists'),
})

export const ScorableCustomField = z.object({
  ...ScorableBaseSchema.shape,
  field: CustomFieldMetricSchema,
  type: z.literal('exists'),
})

export const ScorableRatio = z.object({
  ...ScorableBaseSchema.shape,
  field: RatioIdSchema,
  type: z.literal('exists'),
})

const StandardScorableMetricSchemas = z.discriminatedUnion('field', [
  ScorableContactPitch,
  ScorableContactDaysWorked,
  ScorableMeeting,
  ScorableConversation,
])

export const DynamicScorableMetricSchema = z.union([
  ScorableAccountStatus,
  ScorableCustomField,
  ScorableWorkflowStage,
  ScorableRatio,
])
export type DynamicMetricId = z.infer<typeof DynamicScorableMetricSchema>['field']

export const ScorableMetricSchema = z.union([StandardScorableMetricSchemas, DynamicScorableMetricSchema])
export type ScorableMetric = z.infer<typeof ScorableMetricSchema>

export type ScorableMetricItem = Omit<
  z.infer<typeof GameCriteriaSchema>,
  'pointsPerMetric' | 'qualifiers' | 'type' | 'boostMultiplier'
> & {
  /** @example canvass */
  domain: string
  /** @example account stage */
  category: string
}

export const QualifierSchema = z.object({ minPercent: z.number().gt(0).lte(1) }).and(ScorableMetricSchema)
export type GameQualifier = z.infer<typeof QualifierSchema>

export const WorkflowQualifierSchema = z.object({ minPercent: z.number().gt(0).lte(1) }).and(ScorableWorkflowStage)
export type WorkflowGameQualifier = z.infer<typeof WorkflowQualifierSchema>

export const BoostMultiplierSchema = z.object({
  days: z.array(
    z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6), z.literal(7)])
  ),
  multiplier: z.number(),
})
export type BoostMultiplier = z.infer<typeof BoostMultiplierSchema>

export const GameCriteriaSchema = z.intersection(
  z.object({
    pointsPerMetric: z.number(),
    qualifiers: z.array(QualifierSchema).optional(),
    boostMultiplier: BoostMultiplierSchema.optional(),
  }),
  ScorableMetricSchema
)
export type GameCriteria = z.infer<typeof GameCriteriaSchema>

export const WorkflowGameCriteriaSchema = z.intersection(
  z.object({
    pointsPerMetric: z.number(),
    qualifiers: z.array(QualifierSchema).optional(),
    boostMultiplier: BoostMultiplierSchema.optional(),
  }),
  ScorableWorkflowStage
)
export type WorkflowGameCriteria = z.infer<typeof WorkflowGameCriteriaSchema>

export const TiebreakerSchema = z.object({
  startTime: z.string().or(z.number()).optional(),
  endTime: z.string().or(z.number()).optional(),
  metrics: z
    .array(
      z.object({
        metricId: GameMetricIdSchema,
        order: z.number().int().min(1),
      })
    )
    .describe('The ordered list of metrics to use for tiebreakers'),
})
export type GameTiebreakers = z.infer<typeof TiebreakerSchema>
